import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/input'
import { PAGE_SIZES } from '@/lib/use-pagination'

/**
 * Pagination bar for the console tables: a count, a page-size selector and prev/next.
 *
 * Numbered page buttons are deliberately absent — the lists are searchable and sorted, so jumping to
 * "page 7" is not a real need, and the bar stays readable on a narrow screen.
 */
export function Pagination({
  page,
  pageCount,
  pageSize,
  total,
  firstShown,
  lastShown,
  onPage,
  onPageSize,
  /** What is being counted, in the genitive plural: "пунктов", "категорий". */
  itemsLabel,
}: {
  page: number
  pageCount: number
  pageSize: number
  total: number
  firstShown: number
  lastShown: number
  onPage: (page: number) => void
  onPageSize: (size: number) => void
  itemsLabel: string
}) {
  // One short page needs no controls at all; the count alone is enough.
  const showControls = total > PAGE_SIZES[0]

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3">
      <span className="text-muted-foreground text-xs">
        {total === 0
          ? `Нет ${itemsLabel}`
          : `${firstShown}–${lastShown} из ${total} ${itemsLabel}`}
      </span>

      {showControls ? (
        <div className="flex items-center gap-2">
          <label className="text-muted-foreground flex items-center gap-2 text-xs">
            На странице
            <Select
              value={String(pageSize)}
              onChange={(event) => onPageSize(Number(event.target.value))}
              className="h-8 w-20"
              aria-label="Строк на странице"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPage(page - 1)}
            disabled={page <= 1}
            aria-label="Предыдущая страница"
          >
            <ChevronLeft />
          </Button>
          <span className="text-xs tabular-nums">
            {page} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPage(page + 1)}
            disabled={page >= pageCount}
            aria-label="Следующая страница"
          >
            <ChevronRight />
          </Button>
        </div>
      ) : null}
    </div>
  )
}
